import { Stack, Typography } from "@mui/material";
import AnalystResponseCard from "./AnalystResponseCard";
import { CheckCircle, Cancel } from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import { motion, AnimatePresence } from "motion/react";
import { extractStringValue, extractNumericValue, parsePartialJSON } from "./utils/jsonParsingUtils";
import { useResponseFallback } from "./utils/responseUtils";

interface ReviewerResponseProps {
  response: string;
}

interface ReviewerResponseData {
  queryanswered?: number; // 1 or 0
  reason?: string;
}

// Helper function to extract partial reviewer response fields
const extractReviewerPartialFields = (jsonString: string): Partial<ReviewerResponseData> | null => {
  const partial: Partial<ReviewerResponseData> = {};

  // Extract queryanswered (1 or 0)
  const queryAnswered = extractNumericValue(jsonString, 'queryanswered');
  if (queryAnswered !== null) {
    partial.queryanswered = queryAnswered;
  }

  // Extract reason
  const reason = extractStringValue(jsonString, 'reason');
  if (reason !== null) {
    partial.reason = reason;
  }

  return Object.keys(partial).length > 0 ? partial : null;
};

function ReviewerResponse({ response }: ReviewerResponseProps) {
  const theme = useTheme();

  const reviewerData = parsePartialJSON<ReviewerResponseData>(response, extractReviewerPartialFields);
  const fallback = useResponseFallback({ response, parsedData: reviewerData });

  if (fallback.render || !reviewerData) {
    return fallback.render || <></>;
  }

  const isQueryAnswered = reviewerData.queryanswered === 1;
  const successColor = theme.palette.success.main;
  const failureColor = theme.palette.error.main;

  return (
    <Stack spacing={2}>
      <AnimatePresence>
        <motion.div
          key="reviewer-response"
          initial={{ opacity: 0, scale: 0.8, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.8, y: -20 }}
          transition={{
            duration: 0.5,
            ease: "easeOut",
          }}
        >
          <AnalystResponseCard
            title="Query Review"
            content={
              <Stack spacing={2}>
                {reviewerData.queryanswered !== undefined && (
                  <Stack direction="row" alignItems="center" spacing={1}>
                    {isQueryAnswered ? (
                      <>
                        <CheckCircle sx={{ color: successColor }} />
                        <Typography variant="body1" sx={{ fontWeight: 'bold', color: successColor }}>
                          Query Answered
                        </Typography>
                      </>
                    ) : (
                      <>
                        <Cancel sx={{ color: failureColor }} />
                        <Typography variant="body1" sx={{ fontWeight: 'bold', color: failureColor }}>
                          Query Not Answered
                        </Typography>
                      </>
                    )}
                  </Stack>
                )}

                {reviewerData.reason && (
                  <Typography variant="body2">
                    {reviewerData.reason}
                  </Typography>
                )}
              </Stack>
            }
            icon={isQueryAnswered ? <CheckCircle sx={{ color: successColor }} /> : <Cancel sx={{ color: failureColor }} />}
            borderColor={isQueryAnswered ? successColor : failureColor}
          />
        </motion.div>
      </AnimatePresence>
    </Stack>
  );
}

export default ReviewerResponse;

